import { Body6 } from "@/src/components/typography/components/Body";
import { BodyName } from "@/src/components/typography/components/Body/types";
import { Title7 } from "@/src/components/typography/components/Title";
import {
  formatCentsToUSDWithCurrency,
  formatToUSDWithCurrency,
} from "@/src/helpers/numberFormatters";
import clsx from "clsx";
import { PropsWithChildren } from "react";
import {
  ORDER_NUMBER_TEST_ID,
  ORDER_QUANTITY_TEST_ID,
  ORDER_SUBTOTAL_TEST_ID,
  ORDER_TAX_TEST_ID,
  ORDER_TOTAL_TEST_ID,
  PRODUCT_NAME_TEST_ID,
  PRODUCT_PRICE_TEST_ID,
} from "../../../constants";

export function DetailSection({
  children,
  className,
}: PropsWithChildren & { className?: string }) {
  return (
    <section
      className={clsx("w-full py-6 border-b border-gray-200", className)}
    >
      {children}
    </section>
  );
}

export function DetailLineItem({
  children,
  className,
}: PropsWithChildren & { className?: string }) {
  return (
    <div className={clsx("flex justify-between w-full", className)}>
      {children}
    </div>
  );
}

export function OrderNumber({ number }: { number: string }) {
  return (
    <DetailLineItem className="flex-wrap">
      <Title7 className="mobile:text-md laptop:text-md">Order Number</Title7>
      <Title7
        data-testid={ORDER_NUMBER_TEST_ID}
        className="laptop:text-md font-medium"
      >{`#${number}`}</Title7>
    </DetailLineItem>
  );
}

export function ProductDetails({
  name,
  price,
}: {
  name: string;
  price: string;
}) {
  return (
    <DetailLineItem>
      <Body6 data-testid={PRODUCT_NAME_TEST_ID} md={BodyName.Body_9}>
        {name}
      </Body6>
      <Body6 data-testid={PRODUCT_PRICE_TEST_ID} md={BodyName.Body_9}>
        {formatToUSDWithCurrency(parseFloat(price))}
      </Body6>
    </DetailLineItem>
  );
}

export function OrderQuantity({ quantity }: { quantity: number }) {
  return (
    <DetailLineItem>
      <Body6
        data-testid={ORDER_QUANTITY_TEST_ID}
        md={BodyName.Body_9}
        className="text-gray-350"
      >
        {`QUANTITY: ${quantity}`}
      </Body6>
    </DetailLineItem>
  );
}

export function OrderSubtotal({ subtotal }: { subtotal: string }) {
  return (
    <DetailLineItem>
      <Body6 md={BodyName.Body_9} className="text-gray-350">
        Subtotal
      </Body6>
      <Body6
        data-testid={ORDER_SUBTOTAL_TEST_ID}
        md={BodyName.Body_9}
        className="text-gray-350"
      >
        {formatCentsToUSDWithCurrency(parseFloat(subtotal))}
      </Body6>
    </DetailLineItem>
  );
}

export function OrderTax({ tax }: { tax: string }) {
  return (
    <DetailLineItem>
      <Body6 md={BodyName.Body_9} className="text-gray-350">
        Tax
      </Body6>
      <Body6
        data-testid={ORDER_TAX_TEST_ID}
        md={BodyName.Body_9}
        className="text-gray-350"
      >
        {formatCentsToUSDWithCurrency(parseFloat(tax))}
      </Body6>
    </DetailLineItem>
  );
}

export function OrderTotal({ total }: { total: string }) {
  return (
    <DetailLineItem className="mt-6">
      <Title7 className="mobile:text-md laptop:text-md">Total</Title7>
      <Title7
        data-testid={ORDER_TOTAL_TEST_ID}
        className="mobile:text-md laptop:text-md"
      >
        {formatCentsToUSDWithCurrency(parseFloat(total))}
      </Title7>
    </DetailLineItem>
  );
}
